
import React from 'react';
import { Link } from 'react-router-dom';
import { Star, Zap, ShoppingCart, Twitter, Instagram, Facebook } from 'lucide-react';
import HeroSlider from './HeroSlider';

// Placeholder data for featured designs
const featuredDesigns = [
  {
    id: 1,
    name: 'Cosmic Cat',
    price: '$29.99',
    imageUrl: 'https://placehold.co/400x400/333/fff?text=Cosmic+Cat',
  },
  {
    id: 2,
    name: 'Retro Robot',
    price: '$29.99',
    imageUrl: 'https://placehold.co/400x400/444/fff?text=Retro+Robot',
  },
  {
    id: 3,
    name: 'Floral Skull',
    price: '$29.99',
    imageUrl: 'https://placehold.co/400x400/555/fff?text=Floral+Skull',
  },
  {
    id: 4,
    name: 'Abstract City',
    price: '$29.99',
    imageUrl: 'https://placehold.co/400x400/666/fff?text=Abstract+City',
  },
];

const sliderImages = [
  '/pictures/new_tshirt/0338edf61535473007705c3d24d0b63f_preview.png',
  '/pictures/new_tshirt/399d9232ce4f1e7ba8fa3d8615c90f16_preview.png',
  '/pictures/new_tshirt/5cb0f0cfb457ce1460d82f03a092fc21_preview.png',
  '/pictures/new_tshirt/ba88ff0bfcb8ba4720a1d0efd48fa53a_preview.png',
  '/pictures/new_tshirt/cat_preview.png',
  '/pictures/new_tshirt/Center_Chest_Lion.png',
  '/pictures/new_tshirt/Center_chest_Tshirt.png',
  '/pictures/new_tshirt/Eelgant_figher-Tshirt.png',
  '/pictures/new_tshirt/Elegant_fighter_righ_chest.png',
  '/pictures/new_tshirt/Lion_Arm_Embroidery.png',
  '/pictures/new_tshirt/Lion_King_Hoody.png',
  '/pictures/new_tshirt/Lion-king-tshirt-2.png',
  '/pictures/new_tshirt/Lion-King-tshirt.png',
];

const NewLandingPage = () => {
  return (
    <div className="bg-white text-gray-800">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center text-center">
        <div className="absolute inset-0">
          <HeroSlider images={sliderImages} />
        </div>
        <div className="absolute inset-0 bg-black opacity-50"></div>
        <div className="relative z-10 p-6 pt-16">
          <h1 className="text-5xl md:text-7xl font-extrabold text-white mb-4">
            Wear Your Imagination
          </h1>
          <p className="text-xl md:text-2xl text-gray-200 mb-8">
            AI-Powered Custom T-Shirts, Designed by You.
          </p>
          <Link
            to="/design"
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 px-10 rounded-full text-lg transition duration-300 ease-in-out transform hover:scale-105 shadow-lg"
          >
            Create Your Own T-Shirt
          </Link>
        </div>
      </section>

      {/* Featured Designs Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto">
          <h2 className="text-4xl font-bold text-center text-gray-800 mb-12">
            Shop Our Designs
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8">
            {featuredDesigns.map((design) => (
              <div key={design.id} className="bg-white rounded-lg shadow-lg overflow-hidden transform hover:-translate-y-2 transition-transform duration-300">
                <img src={design.imageUrl} alt={design.name} className="w-full h-64 object-cover" />
                <div className="p-6 text-center">
                  <h3 className="text-xl font-semibold mb-2">{design.name}</h3>
                  <p className="text-lg text-gray-600 mb-4">{design.price}</p>
                  <button className="w-full bg-gray-800 hover:bg-gray-900 text-white font-bold py-2 px-4 rounded-full transition-colors">
                    Buy Now
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="bg-gray-50 py-20 px-4">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl font-bold text-gray-800 mb-12">How It Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="flex flex-col items-center">
              <div className="bg-blue-600 text-white rounded-full p-6 mb-4">
                <Zap size={40} />
              </div>
              <h3 className="text-2xl font-semibold mb-2">1. Imagine & Generate</h3>
              <p className="text-gray-600">Use our AI to bring your wildest t-shirt ideas to life from a simple text prompt.</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="bg-blue-600 text-white rounded-full p-6 mb-4">
                <ShoppingCart size={40} />
              </div>
              <h3 className="text-2xl font-semibold mb-2">2. Customize Your Fit</h3>
              <p className="text-gray-600">Choose your t-shirt style, color, and fit. Place your design exactly where you want it.</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="bg-blue-600 text-white rounded-full p-6 mb-4">
                <Star size={40} />
              </div>
              <h3 className="text-2xl font-semibold mb-2">3. Order & Wear</h3>
              <p className="text-gray-600">Get your unique creation delivered to your door. High-quality printing, guaranteed.</p>
            </div>
          </div>
        </div>
      </section>

      {/* AI Showcase Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto">
          <div className="flex flex-col md:flex-row items-center gap-12">
            <div className="md:w-1/2">
              <h2 className="text-4xl font-bold text-gray-800 mb-6">The Magic of AI</h2>
              <p className="text-lg text-gray-600 mb-4">
                You don't need to be an artist to create a masterpiece. Our AI can generate stunning, unique artwork from your words.
              </p>
              <div className="bg-gray-100 p-4 rounded-lg">
                <p className="font-mono text-gray-700">"A majestic lion wearing a crown, cinematic style"</p>
              </div>
            </div>
            <div className="md:w-1/2">
              <img src="https://placehold.co/600x400/ddd/333?text=AI+Generated+Image" alt="AI Generated T-Shirt Design" className="rounded-lg shadow-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="bg-gray-50 py-20 px-4">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl font-bold text-gray-800 mb-12">What Our Customers Are Saying</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <div className="flex items-center justify-center mb-4">
                {[...Array(5)].map((_, i) => <Star key={i} className="text-yellow-500" />)}
              </div>
              <p className="text-gray-600 mb-4">"I'm not creative at all, but I made a shirt that all my friends are jealous of. So cool!"</p>
              <p className="font-semibold">- Alex Johnson</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <div className="flex items-center justify-center mb-4">
                {[...Array(5)].map((_, i) => <Star key={i} className="text-yellow-500" />)}
              </div>
              <p className="text-gray-600 mb-4">"The quality of the t-shirt is amazing, and the print is perfect. Will be ordering more!"</p>
              <p className="font-semibold">- Maria Garcia</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <div className="flex items-center justify-center mb-4">
                {[...Array(5)].map((_, i) => <Star key={i} className="text-yellow-500" />)}
              </div>
              <p className="text-gray-600 mb-4">"The AI is so much fun to play with. I've made designs for my whole family."</p>
              <p className="font-semibold">- David Chen</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-12 px-4">
        <div className="container mx-auto text-center">
          <h3 className="text-2xl font-bold mb-4">AI T-Shirt Design</h3>
          <p className="text-gray-400 mb-6">Create, wear, and share your imagination.</p>
          <div className="flex justify-center space-x-6 mb-8">
            <Link to="#" className="text-gray-400 hover:text-white"><Twitter /></Link>
            <Link to="#" className="text-gray-400 hover:text-white"><Instagram /></Link>
            <Link to="#" className="text-gray-400 hover:text-white"><Facebook /></Link>
          </div>
          <div className="text-gray-500">
            <p>&copy; {new Date().getFullYear()} AI T-Shirt Design. All Rights Reserved.</p>
            <div className="mt-4">
              <Link to="/profile" className="text-gray-500 hover:text-white mx-2">About Us</Link>
              <Link to="/terms" className="text-gray-500 hover:text-white mx-2">Terms</Link>
              <Link to="/refund-policy" className="text-gray-500 hover:text-white mx-2">Refunds</Link>
              <Link to="/privacy" className="text-gray-500 hover:text-white mx-2">Privacy</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default NewLandingPage;
